import {
  useStateXAtom,
  useStateXAtomValue,
  useStateXValueSetter,
} from '@cloudio-saas/statex';
import { Button, Grid, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { expressionPropsAtom } from 'expression/builder/state';
import { useExpressionWithError } from 'expression/useExpression';
import stringify from 'json-stable-stringify';
import { ExpressionReturnType } from 'layout/Sidebar/panels/PropertiesView/property-editors/types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { getErrorMessage, isEmpty } from 'utils/isEmpty';
import { PubSub } from 'utils/PubSub';
import { EventMap, expAtom } from './ExpressionInput';

const useStyles = makeStyles((theme: Theme) => ({
  actions: {
    backgroundColor: theme.palette.background.default,
    padding: 12,
  },
  result: {
    paddingLeft: 16,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  error: {
    color: theme.palette.error.main,
  },
  mr: {
    marginRight: 8,
  },
}));

export default function ExpressionActions({
  pubsub,
}: {
  pubsub: PubSub<EventMap>;
}) {
  const classes = useStyles();

  const [expressionProps, setExpressionProps] =
    useStateXAtom(expressionPropsAtom);

  useEffect(() => {
    return () => {
      // cleanup on exit
      setExpressionProps(expressionPropsAtom.defaultValue);
    };
  }, [setExpressionProps]);

  const {
    path,
    type: expressionType,
    returnType,
    context: expressionContext,
  } = expressionProps;
  const saveExpression = useStateXValueSetter(path);
  const value = useStateXAtomValue(expAtom);

  const { value: expValue, error } = useExpressionWithError({
    exp: { type: 'expression', value },
    context: { ...expressionContext, itemId: 'ExpressionBuilder' },
    donotShowError: true,
  });

  const handleClose = () => {
    setExpressionProps((props) => ({
      ...props,
      open: false,
    }));
  };

  const onSubmit = () => {
    saveExpression(
      expressionType === 'string' ? value : { type: 'expression', value },
    );
    // @ts-ignore fn call
    expressionContext.data.onSubmit?.(value);
    handleClose();
  };

  const handleClear = () => {
    pubsub.pub('clear', undefined);
    saveExpression(undefined);
  };

  const decode = useCallback(
    (val: unknown): { value?: string | number; error?: string } => {
      if (isEmpty(val)) {
        return {};
      }
      if (val instanceof Error) {
        return { error: getErrorMessage(val) };
      }
      if (typeof val === 'object' && val !== null) {
        const v = val as Record<string, unknown>;
        if (v.name && v.message && v.stack) {
          return {
            error: `${v.name}: ${v.message}`,
          };
        }
      }
      if (
        !(
          [
            'any',
            'array',
            'date',
            'object',
            'page',
            'tf',
            'user',
            'yn',
          ] as ExpressionReturnType[]
        ).includes(returnType) &&
        typeof val !== returnType
      ) {
        return {
          value: undefined,
          error: `Invalid return type '${typeof val}'! Expected a value of type '${returnType}'.`,
        };
      }
      if (returnType === 'array' && !Array.isArray(val)) {
        return {
          value: undefined,
          error: `Invalid return type '${typeof val}'! Expected an array!.`,
        };
      }
      if (
        typeof val === 'object' &&
        (['any', 'array', 'object'] as ExpressionReturnType[]).includes(
          returnType,
        )
      ) {
        return {
          value: stringify(val),
          error: undefined,
        };
      }
      switch (typeof val) {
        case 'string':
        case 'number':
          return { value: val, error: undefined };
        case 'boolean':
          return { value: val ? 'true' : 'false', error: undefined };
        default:
          return {
            value: undefined,
            error: `Invalid return type ${typeof val}. Only string, number or boolean supported as an expression return type!`,
          };
      }
    },
    [returnType],
  );

  const { value: curValue, error: curError } = useMemo(
    () => decode(expValue),
    [decode, expValue],
  );

  const expError = error || curError;
  const expResult = expError || curValue;

  return (
    <div>
      <Grid container item xs alignItems="center" className={classes.actions}>
        <Grid
          item
          xs
          sx={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span>Result:</span>
          <span
            className={clsx(classes.result, expError ? classes.error : null)}
          >
            {expResult}
          </span>
        </Grid>
        <Grid item xs="auto">
          <Button
            color="primary"
            onClick={handleClear}
            variant="text"
            className={classes.mr}
          >
            Clear
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!!expError}
            color="primary"
            variant="contained"
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </div>
  );
}
